import React from 'react';

import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import CardNE from '../../../../../shared/components/CardNE/CardNE';
import StepperNE from '../../../../../shared/components/StepperNE/StepperNE';
import Tag from '../../../../../shared/components/Tag/Tag';
import {
  REWARDS_ORDER_HISTORY_BALANCE_STEPS,
  REWARDS_ORDER_HISTORY_POINTS_TOOLTIP_TEXT,
  REWARDS_ORDER_HISTORY_STATUS_TAG_TYPES
} from '../../../../../shared/constants/rewards';
import { ICustomTooltipNEProps } from '../../../../../shared/interfaces/global';
import { IRewardsOrderInfo } from '../../../../../shared/interfaces/rewards';
import { TRANSLATION_ORDER_HISTORY_DETAIL } from '../../../../../shared/translations/rewards';
import { RewardsBalancePaymentMethods } from '../../../../../shared/types/rewards';
import { toLocaleNumberFormat } from '../../../../../shared/utils/utils';

interface OrderDetailsTableProps {
  orderInfo: IRewardsOrderInfo | undefined;
  loading: boolean;
  isOutgoings: boolean;
}

const OrderInfoCard = ({ orderInfo, loading, isOutgoings }: OrderDetailsTableProps) => {
  const { t } = useTranslation();

  const getPaymentMethod = () => {
    if (!orderInfo) {
      return '';
    }
    if (orderInfo.paymentMethod === RewardsBalancePaymentMethods[RewardsBalancePaymentMethods.balance]) {
      return t(TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.paymentMethod.balance);
    }
    if (orderInfo.paymentMethod === RewardsBalancePaymentMethods[RewardsBalancePaymentMethods['credit-line']]) {
      return t(TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.paymentMethod.credit);
    }
    if (orderInfo.paymentMethod === RewardsBalancePaymentMethods[RewardsBalancePaymentMethods['bank-transfer']]) {
      return t(TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.paymentMethod.transfer);
    }
  };

  const getTooltipInfo = (orderInfo: IRewardsOrderInfo) => {
    if (!isOutgoings) {
      if (orderInfo.status === 'voided') {
        return;
      }
      return {
        placement: 'right',
        tooltipTitle: (
          <StepperNE
            labels={[
              t(TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.steps.received),
              t(TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.steps.invoiced),
              t(TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.steps.validated),
              t(TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.steps.approved)
            ]}
            active={REWARDS_ORDER_HISTORY_BALANCE_STEPS[orderInfo.status]}
            orientation="vertical"
          />
        )
      } as ICustomTooltipNEProps;
    }
    return {
      placement: 'top',
      tooltipTitle: <div className="tooltip-text">{t(REWARDS_ORDER_HISTORY_POINTS_TOOLTIP_TEXT[orderInfo.status])}</div>
    } as ICustomTooltipNEProps;
  };

  if (loading) {
    return <Skeleton className="rounded-3xl" variant="rectangular" height={170} />;
  }

  return (
    <CardNE className="justify-between gap-6" data-experience="new">
      <h3 className="ne-font-body-lg font-bold m-0">
        {t(TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.title, { order: orderInfo?.orderNumber })}
      </h3>
      <div className="flex flex-row justify-between w-full">
        <div className="flex flex-column">
          {orderInfo && (
            <>
              <h4 className="ne-font-body-sm font-bold m-0 py-2">
                {t(TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.table.status)}
              </h4>
              <div className="py-2">
                <Tag
                  text={t(
                    TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.status[orderInfo.transactionType][orderInfo.status]
                  )}
                  type={REWARDS_ORDER_HISTORY_STATUS_TAG_TYPES[orderInfo.transactionType][orderInfo.status]}
                  tooltip={getTooltipInfo(orderInfo)}
                />
              </div>
            </>
          )}
        </div>
        {isOutgoings && (
          <div className="flex flex-column">
            <h4 className="ne-font-body-sm font-bold m-0 py-2">
              {t(TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.table.charges)}
            </h4>
            <p className="ne-font-body-sm m-0 py-2">
              {orderInfo?.orderLoads + ' ' + t(TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.table.values.charges)}
            </p>
          </div>
        )}
        <div className="flex flex-column">
          <h4 className="ne-font-body-sm font-bold m-0 py-2">
            {t(TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.table.sendDate)}
          </h4>
          <p className="ne-font-body-sm m-0 py-2">{orderInfo?.createdAt}</p>
        </div>
        <div className="flex flex-column">
          <h4 className="ne-font-body-sm font-bold m-0 py-2">
            {t(TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.table.paymentType)}
          </h4>
          <p className="ne-font-body-sm m-0 py-2">{getPaymentMethod()}</p>
        </div>
        <div className="flex flex-column">
          <h4 className="ne-font-body-sm font-bold m-0 py-2">
            {t(TRANSLATION_ORDER_HISTORY_DETAIL.orderInfo.table.total)}
          </h4>
          <p className="ne-font-body-sm m-0 py-2">
            {orderInfo
              ? toLocaleNumberFormat(orderInfo.amount, { style: 'currency', currency: orderInfo.currency })
              : ''}
          </p>
        </div>
        <div />
      </div>
    </CardNE>
  );
};

export default OrderInfoCard;
